import { Link } from '@tanstack/react-router';

import type { CreativePin, CreativeRevisionSnapshot, CreativeStatus } from '@/api/types';
import { CopyableId } from '@/components/copyable-id';
import { QueryErrorNote } from '@/components/error-panel';
import { StatusPill } from '@/components/status-pill';
import { campaignStatusLabel, campaignPinKind } from '@/lib/campaign-status';
import {
  assetDelta,
  placementLabel,
  primaryRevisionAsset,
  revisionFieldRows,
} from '@/lib/creative-revision-diff';
import { fmtDate } from '@/lib/datetime';

/**
 * A campaign a newer version can still be applied to: published (live or
 * paused) and with a flight left. A finished campaign takes no roll-out —
 * its creatives can't change — so it is never told to apply one (2026-10-19).
 */
function canTakeNewerVersion(pin: CreativePin): boolean {
  return (pin.campaign_status === 'live' || pin.campaign_status === 'paused')
    && pin.operational_status?.phase !== 'finished';
}

export interface CreativeVersionsPanelProps {
  channel: string;
  currentRevision: number;
  approvedRevision: number | null;
  /** The creative's own status: an archived creative takes no edits until it is restored. */
  creativeStatus?: CreativeStatus;
  revisions: CreativeRevisionSnapshot[] | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
}

/**
 * The archive: every revision newest first, what changed against the one
 * before, who made and approved it, every campaign revision that pinned it,
 * and the provider objects it became. A pure render — the drawer owns the
 * query — so the DOM test needs no mocks.
 */
export function CreativeVersionsPanel({
  channel,
  currentRevision,
  approvedRevision,
  creativeStatus,
  revisions,
  isPending,
  error,
  onRetry,
}: CreativeVersionsPanelProps) {
  if (error) return <QueryErrorNote error={error} what="versions" onRetry={onRetry} />;
  if (isPending || !revisions) {
    return (
      <div className="creative-versions" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="creative-version creative-version--skeleton" />
        ))}
        <div className="muted" style={{ fontSize: 11.5 }}>
          Loading versions…
        </div>
      </div>
    );
  }
  if (revisions.length === 0) {
    return (
      <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
        No versions recorded yet.
      </div>
    );
  }
  // A published campaign still pins an older version. This is edit eligibility,
  // independent of its observed delivery state. The note
  // below it says why saving alone did not change delivery.
  const publishedOnOlder = revisions.find(
    (r) => r.revision !== currentRevision && r.pins.some(canTakeNewerVersion),
  );
  return (
    <div className="creative-versions">
      {revisions.map((revision, index) => (
        <VersionCard
          key={revision.revision}
          channel={channel}
          revision={revision}
          previous={revisions[index + 1] ?? null}
          isCurrent={revision.revision === currentRevision}
          isApproved={revision.revision === approvedRevision}
        />
      ))}
      {/* Only an approved version is frozen; a draft or one in review takes edits in place. */}
      {revisions.length === 1 && creativeStatus !== 'archived' && (
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
          {approvedRevision === currentRevision
            ? 'Editing the assets, copy or destination of an approved creative creates version 2 here.'
            : creativeStatus === 'review'
              ? 'Edits save into this version until it is approved.'
              : 'Edits save into this draft until it is approved.'}
        </div>
      )}
      {publishedOnOlder && (
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
          {publishedOnOlder.pins.find(canTakeNewerVersion)?.campaign_name} is on v
          {publishedOnOlder.revision}. Open that campaign, review the new version, then explicitly apply it. Other campaigns keep their current versions.
        </div>
      )}
    </div>
  );
}

function VersionCard({
  channel,
  revision,
  previous,
  isCurrent,
  isApproved,
}: {
  channel: string;
  revision: CreativeRevisionSnapshot;
  previous: CreativeRevisionSnapshot | null;
  isCurrent: boolean;
  isApproved: boolean;
}) {
  const rows = revisionFieldRows(channel, previous, revision);
  const changed = rows.filter((r) => r.changed);
  const unchanged = rows.filter((r) => !r.changed);
  const assets = previous ? assetDelta(previous.assets, revision.assets) : [];
  const thumb = primaryRevisionAsset(revision.assets);
  const noContentChange = previous !== null && changed.length === 0 && assets.length === 0;
  return (
    <article className="creative-version" data-revision={revision.revision}>
      <header className="creative-version-head">
        <span className="mono creative-version-num">v{revision.revision}</span>
        <StatusPill status={revision.status} />
        {isCurrent && (
          <span className="tag creative-version-tag creative-version-tag--current">Current</span>
        )}
        {isApproved && !isCurrent && <span className="tag creative-version-tag">Approved</span>}
        <span className="mono creative-version-when">
          {revision.approved_at
            ? `Approved ${fmtDate(revision.approved_at)}${revision.approved_by_name ? ` · ${revision.approved_by_name}` : ''}`
            : `Created ${fmtDate(revision.created_at)}${revision.created_by_name ? ` · ${revision.created_by_name}` : ''}`}
        </span>
      </header>
      <div className="creative-version-body">
        <div className="creative-version-thumb" aria-hidden="true">
          {thumb?.view_url ? (
            thumb.kind === 'video' ? (
              <video src={thumb.view_url} muted playsInline />
            ) : (
              <img src={thumb.view_url} alt="" loading="lazy" />
            )
          ) : (
            <span className="muted" style={{ fontSize: 10 }}>
              No preview
            </span>
          )}
        </div>
        <div className="creative-version-diff">
          {previous === null ? (
            <>
              <div className="creative-version-lbl">First version</div>
              {rows
                .filter((r) => r.after)
                .map((r) => (
                  <div key={r.key} className="creative-version-row">
                    <span className="creative-version-field">{r.label}</span> {r.after}
                  </div>
                ))}
            </>
          ) : noContentChange ? (
            <div className="creative-version-lbl">No content changes</div>
          ) : (
            <>
              <div className="creative-version-lbl">Changes vs v{previous.revision}</div>
              {changed.map((r) => (
                <div key={r.key} className="creative-version-row">
                  <span className="creative-version-field">{r.label}:</span>{' '}
                  {r.tokens.map((t, i) =>
                    t.kind === 'del' ? (
                      <del key={i} aria-label="removed">
                        {t.text}
                      </del>
                    ) : t.kind === 'ins' ? (
                      <ins key={i} aria-label="added">
                        {t.text}
                      </ins>
                    ) : (
                      <span key={i}>{t.text}</span>
                    ),
                  )}
                </div>
              ))}
              {assets.length > 0 && (
                <div className="creative-version-row">
                  <span className="creative-version-field">Assets:</span>{' '}
                  {assets.map((change, i) => (
                    <span key={change.placement_tag}>
                      {i > 0 && ' · '}
                      {placementLabel(change.placement_tag)}{' '}
                      {change.kind === 'added' ? (
                        <ins aria-label="added">added</ins>
                      ) : change.kind === 'removed' ? (
                        <del aria-label="removed">removed</del>
                      ) : (
                        <ins aria-label="replaced">replaced</ins>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {unchanged.length > 0 && (
                <div className="creative-version-row muted">
                  <span className="creative-version-field">Unchanged:</span>{' '}
                  {unchanged.map((r) => r.label).join(', ')}
                  {assets.length === 0 && revision.assets.length > 0
                    ? `, ${revision.assets.length} asset${revision.assets.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <footer className="creative-version-foot">
        <div className="creative-version-uses">
          {revision.pins.length === 0 ? (
            <span className="muted" style={{ fontSize: 11.5 }}>
              Not used by any campaign.
            </span>
          ) : (
            revision.pins.map((pin) => (
              <Link
                key={`${pin.campaign_id}:${pin.campaign_revision}:${pin.replaced_at ?? 'now'}`}
                to="/campaigns/$campaignId"
                params={{ campaignId: pin.campaign_id }}
                className="chip creative-version-use"
                data-status={pin.campaign_status}
              >
                <span className="dot" aria-hidden="true" />
                {pin.campaign_name} · rev {pin.campaign_revision} ·{' '}
                {campaignStatusLabel(campaignPinKind(pin))}
                {pin.replaced_at ? ` · until ${fmtDate(pin.replaced_at)}` : ''}
              </Link>
            ))
          )}
        </div>
        {revision.provider_objects.map((object) => (
          <div
            key={`${object.target}:${object.provider_creative_id}`}
            className="creative-version-id"
          >
            <span className="mono creative-version-field">
              {object.target === 'meta' ? 'Meta · AdCreative' : 'TTD · Creative'}
            </span>
            <CopyableId value={object.provider_creative_id} size="touch" />
          </div>
        ))}
      </footer>
    </article>
  );
}
